import React, { useState, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Sidebar } from './Sidebar';
import { Menu, Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { getUnreadCount } from '../../lib/api';
import { Toaster } from '../ui/sonner';

export const DashboardLayout = ({ requiredRole }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadCount();
      setUnreadMessages(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  return (
    <div className="app-layout">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        unreadMessages={unreadMessages}
      />
      
      <div className="main-content">
        {/* Topbar */}
        <header className="topbar">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-slate-400 hover:text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold text-white hidden sm:block">
              {user?.role === 'admin' ? 'Admin Portal' : 'Client Portal'}
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="relative text-slate-400 hover:text-white"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadMessages > 0 && (
                <span className="notification-badge">{unreadMessages}</span>
              )}
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet context={{ refreshUnread: fetchUnreadCount }} />
        </main>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
};
